import React, { useState } from 'react';
import { Modal, Form, Select, Input, Radio, message, Space, Typography } from 'antd';
import { orderAuditApi } from '../../../../services/process/order-audit';
import { OperationAction } from '../../../../types/process/operation-log';
import ResourceUploader from '../../../../components/Business/ResourceUploader';

const { Text } = Typography;

interface AuditInterruptModalProps {
  visible: boolean;
  orderNo: string;
  auditPayload?: any; // The current form data to save
  onCancel: () => void;
  onSuccess: () => void;
}

const CANCEL_TYPES = {
  USER: [
    { label: '用户原因', value: '用户原因' },
    { label: '联系不上用户', value: '联系不上用户' },
    { label: '用户资质问题', value: '用户资质问题' },
  ],
  OTHER: [
    { label: '租金/押金/收益问题', value: '租金/押金/收益问题' },
    { label: '地区原因', value: '地区原因' },
  ],
};

const CANCEL_REASONS: Record<string, string[]> = {
  '用户原因': ['不接受租赁服务设备', '不需要了/不想租了', '买断意愿低', '误下单', '其他'],
  '联系不上用户': ['客户空号/停机', '客户拒接/未接', '多次联系未果'],
  '用户资质问题': ['客户信息/材料造假', '客户逾期/黑名单', '年龄不符', '未成年', '其他'],
  '租金/押金/收益问题': ['无法支付首期', '无法支付押金', '无法接受免密代扣', '嫌租金贵'],
  '地区原因': ['偏远地区不发货', '疫情/不可抗力'],
};

export const AuditInterruptModal: React.FC<AuditInterruptModalProps> = ({
  visible,
  orderNo,
  auditPayload,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        ...auditPayload,
        audit: {
          ...auditPayload?.audit,
          interruptType: values.interruptType,
          interruptReason: values.interruptReason,
          interruptEvidence: values.interruptEvidence || [],
        }
      };

      await orderAuditApi.changeStatus(orderNo, OperationAction.AuditInterrupt, payload);
      message.success('已确认中断审核');
      onSuccess();
    } catch (error: any) {
      if (error?.errorFields) return; // 校验失败
      message.error(error.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <span style={{ color: '#faad14', fontSize: '18px' }}>!</span>
          <span>请选择审核中断原因：</span>
        </Space>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      okText="确认"
      cancelText="取消"
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="interruptType"
          label="取消类型"
          rules={[{ required: true, message: '请选择取消类型' }]}
        >
          <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Space align="start">
              <div style={{
                background: 'rgba(39,133,244,0.1)',
                border: '1px solid rgba(39,133,244,0.36)',
                borderRadius: '5px',
                padding: '2px 8px',
                color: '#2785f4',
                fontSize: '14px',
                marginTop: '4px'
              }}>用户</div>
              <Space wrap>
                {CANCEL_TYPES.USER.map(item => (
                  <Radio key={item.value} value={item.value}>{item.label}</Radio>
                ))}
              </Space>
            </Space>
            <Space align="start" style={{ marginTop: '8px' }}>
              <div style={{
                background: 'rgba(39,133,244,0.1)',
                border: '1px solid rgba(39,133,244,0.36)',
                borderRadius: '5px',
                padding: '2px 8px',
                color: '#2785f4',
                fontSize: '14px',
                marginTop: '4px'
              }}>其他</div>
              <Space wrap>
                {CANCEL_TYPES.OTHER.map(item => (
                  <Radio key={item.value} value={item.value}>{item.label}</Radio>
                ))}
              </Space>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.interruptType !== currentValues.interruptType}
        >
          {({ getFieldValue, setFieldsValue }) => {
            const type = getFieldValue('interruptType');
            const reasons = CANCEL_REASONS[type] || [];
            
            return (
              <Form.Item
                name="interruptReason"
                label="取消原因"
                rules={[{ required: true, message: '请选择取消原因' }]}
              >
                <Select
                  placeholder="请选择取消原因"
                  disabled={!type}
                  onChange={() => {}} // Handle internal state if needed
                >
                  {reasons.map(reason => (
                    <Select.Option key={reason} value={reason}>{reason}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item
          name="interruptEvidence"
          label={<Text>证据截图（如用户沟通截图）:</Text>}
        >
          <ResourceUploader
            dir={`order_audit/${orderNo}/interruptEvidence`}
            maxCount={5}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
